import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Download, RefreshCw, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BUSINESS } from "@/lib/constants";
import { Seo } from "@/components/Seo";

const inputClass =
  "w-full px-3 py-2.5 text-sm border border-[#D0CBC0] rounded focus:outline-none focus:border-spice-green bg-white text-spice-ink transition-colors";

const FIELD_LABELS = [
  ["reference", "Reference"], ["enquiry_type", "Enquiry Type"],
  ["buyer_name", "Buyer"], ["business_name", "Business"], ["mobile", "Mobile"],
  ["whatsapp", "WhatsApp"], ["email", "Email"], ["city", "City"], ["state", "State"],
  ["business_type", "Business Type"], ["product", "Product"], ["grade_spec", "Grade/Spec"],
  ["quantity_kg", "Qty (kg)"], ["frequency", "Frequency"], ["monthly_consumption", "Monthly"],
  ["purchase_date", "Purchase Date"], ["gst_invoice", "GST Invoice"], ["contact_method", "Contact Via"],
  ["message", "Requirement Details"], ["source_page", "Submitted From"],
];

export default function AdminPage() {
  const [admin, setAdmin] = useState(undefined); // undefined = checking, null = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadEnquiries = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/enquiries");
      setEnquiries(data.enquiries);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdmin(null);
        setEnquiries([]);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => setAdmin(null));
  }, []);

  useEffect(() => {
    if (admin) loadEnquiries();
  }, [admin, loadEnquiries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdmin(data);
      toast.success("Logged in");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setAdmin(null);
    setEnquiries([]);
  };

  const handleExport = async () => {
    try {
      const { data } = await api.get("/admin/enquiries/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "nandanaaya_enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  if (admin === undefined) {
    return (
      <div className="min-h-screen bg-spice-cream flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-spice-green" />
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="min-h-screen bg-spice-cream flex items-center justify-center px-4" data-testid="admin-login-page">
        <Seo title="Admin Sign In | Nandanaaya Spices" description="Secure enquiry dashboard." path="/admin" noindex />
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white border border-spice-line rounded-xl shadow-md p-8 space-y-5" data-testid="admin-login-form">
          <div className="text-center space-y-1">
            <ShieldCheck className="w-8 h-8 text-spice-green mx-auto" />
            <h1 className="text-lg font-bold text-spice-ink">Admin — {BUSINESS.name}</h1>
            <p className="text-xs text-spice-muted">Secure enquiry dashboard</p>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#343D37] mb-1" htmlFor="admin-email">Email</label>
            <input id="admin-email" type="email" required className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#343D37] mb-1" htmlFor="admin-password">Password</label>
            <input id="admin-password" type="password" required className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
          </div>
          {loginError && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="admin-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full py-3 bg-spice-green text-white font-bold text-sm uppercase tracking-wider rounded hover:bg-spice-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign In
          </button>
          <a href="/" className="block text-center text-xs text-spice-subtle hover:text-spice-green transition-colors" data-testid="admin-back-home">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spice-cream" data-testid="admin-dashboard">
      <Seo title="Enquiry Dashboard | Nandanaaya Spices" description="Secure enquiry dashboard." path="/admin" noindex />
      <header className="bg-spice-green-dark text-white px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="font-bold tracking-tight">{BUSINESS.nameCaps}</span>
            <span className="text-xs text-spice-sage block">Enquiry Dashboard — {admin.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadEnquiries} data-testid="admin-refresh-button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded border border-white/30 hover:bg-white/10 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={handleExport} data-testid="admin-export-button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded bg-spice-gold text-spice-green-dark hover:bg-spice-gold-light transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded border border-white/30 hover:bg-white/10 transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-spice-ink">Wholesale Enquiries</h2>
          <span className="text-xs text-spice-muted" data-testid="admin-enquiry-count">{enquiries.length} record{enquiries.length === 1 ? "" : "s"}</span>
        </div>

        {loadingList ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-spice-green" /></div>
        ) : enquiries.length === 0 ? (
          <div className="bg-white border border-spice-line rounded-xl p-10 text-center text-sm text-spice-muted" data-testid="admin-empty-state">
            No enquiries yet. Submissions from the website form will appear here.
          </div>
        ) : (
          <div className="space-y-3">
            {enquiries.map((enq) => (
              <details key={enq.enquiry_id} className="bg-white border border-spice-line rounded-lg overflow-hidden" data-testid={`admin-enquiry-${enq.enquiry_id}`}>
                <summary className="cursor-pointer px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs hover:bg-spice-cream transition-colors">
                  <span className="font-mono text-[11px] text-spice-green font-bold">{enq.reference || "—"}</span>
                  <span className="font-bold text-spice-ink">{enq.business_name}</span>
                  <span className="text-spice-green font-semibold">{enq.product}</span>
                  <span className="text-spice-muted">{enq.quantity_kg}</span>
                  <span className="text-spice-muted">{enq.city}, {enq.state}</span>
                  <span className="ml-auto text-spice-subtle">{new Date(enq.created_at).toLocaleString("en-IN")}</span>
                </summary>
                <div className="px-4 pb-4 pt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-xs border-t border-spice-line">
                  {FIELD_LABELS.map(([key, label]) => (
                    <div key={key}>
                      <span className="text-spice-subtle block">{label}</span>
                      <span className="font-medium text-spice-ink break-words">{enq[key] || "—"}</span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
